"use client";

import { useEffect, useRef, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getNearestNodeFromDOMNode,
  $getRoot,
  $insertNodes,
  $isTextNode,
  $nodesOfType,
} from "lexical";
import { ASSET_DRAG_MIME } from "@/components/AssetsEditor/constants";
import { $createMentionNode, MentionNode } from "./MentionNode";
import { ASSET_MENTION_ID_ATTRIBUTE } from "./MentionComponent";
import { useEditorAssets } from "./EditorAssetsContext";

const isAssetDrag = (dataTransfer) =>
  [...(dataTransfer?.types || [])].includes(ASSET_DRAG_MIME);

const draggedAssetIds = (dataTransfer) => {
  try {
    const ids = JSON.parse(dataTransfer.getData(ASSET_DRAG_MIME) || "[]");
    return Array.isArray(ids) ? ids.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
};

// Chromium/WebKit expose caretRangeFromPoint, Firefox caretPositionFromPoint.
// Returns a collapsed DOM range at the pointer, or null when neither is available.
const caretRangeAtPoint = (clientX, clientY) => {
  if (document.caretRangeFromPoint) {
    return document.caretRangeFromPoint(clientX, clientY);
  }
  if (document.caretPositionFromPoint) {
    const position = document.caretPositionFromPoint(clientX, clientY);
    if (!position) return null;
    const range = document.createRange();
    range.setStart(position.offsetNode, position.offset);
    range.collapse(true);
    return range;
  }
  return null;
};

// `caretRangeFromPoint` hit-tests the whole document, not just the editor. A point
// with no glyph directly under it (the empty space below a short line, or the
// editor's own padding) makes the browser fall back to the nearest text node
// *anywhere on the page*, which can be nowhere near the editor (e.g. the side nav).
// Pulling the point inside root's own box first keeps the probe on real content —
// though it isn't enough on its own: see the validity checks below for why.
const clampToRoot = (root, clientX, clientY) => {
  const rect = root.getBoundingClientRect();
  return {
    x: Math.min(Math.max(clientX, rect.left + 1), rect.right - 1),
    y: Math.min(Math.max(clientY, rect.top + 1), rect.bottom - 1),
  };
};

// The only DOM containers worth landing on are actual Lexical TextNodes. A mention
// chip (MentionNode) renders real DOM text for its label — genuinely inside root,
// so a plain `root.contains` check lets the caret land *inside* an otherwise-atomic
// mention — but it isn't a TextNode, so this correctly rejects it. Must run inside
// an editor read/update (the `$`-prefixed calls require an active Lexical context).
const $isValidCaretContainer = (root, domNode) => {
  if (!domNode || !root.contains(domNode)) return false;
  return $isTextNode($getNearestNodeFromDOMNode(domNode));
};

// A collapsed range reports a zero-size box in some browsers, so fall back to the
// line box of the element it sits in.
const rectFromRange = (range) => {
  const rangeRect = range.getClientRects()[0] || range.getBoundingClientRect();
  if (rangeRect && rangeRect.height > 0) {
    return {
      top: rangeRect.top,
      left: rangeRect.left,
      height: rangeRect.height,
    };
  }
  const container = range.startContainer;
  const element =
    container.nodeType === Node.ELEMENT_NODE
      ? container
      : container.parentElement;
  if (!element) return null;
  const elementRect = element.getBoundingClientRect();
  return {
    top: elementRect.top,
    left: rangeRect?.left ?? elementRect.left,
    height: elementRect.height,
  };
};

// Last line of defense: even after the checks above, only trust a rect that's
// actually on screen over the editor. Catches anything the DOM/Lexical checks
// don't (a mismatched node tree, a stale root reference, ...) — the indicator
// should never be able to render outside the box it's meant to be inside of.
const isRectOverRoot = (root, rect) => {
  const rootRect = root.getBoundingClientRect();
  return (
    rect.left >= rootRect.left - 1 &&
    rect.left <= rootRect.right + 1 &&
    rect.top >= rootRect.top - 1 &&
    rect.top <= rootRect.bottom + 1
  );
};

// Viewport coordinates, for a fixed-position indicator that needs no origin
// arithmetic. Null whenever the point can't be resolved to a real caret position
// inside the editor's own text — showing nothing beats showing the wrong thing.
const caretRectAtPoint = (editor, root, clientX, clientY) => {
  const { x, y } = clampToRoot(root, clientX, clientY);
  const range = caretRangeAtPoint(x, y);
  if (!range) return null;

  // `$isValidCaretContainer` maps a DOM node back to its Lexical node via
  // `getActiveEditor()`, which plain `editorState.read()` leaves unset (null) —
  // it only gets set when `{ editor }` is passed, which is exactly what
  // `editor.read()` does under the hood. Using `getEditorState().read()` here
  // instead throws (`null._key`) and silently aborts this whole function before
  // ever showing an indicator.
  let valid = false;
  editor.read(() => {
    valid = $isValidCaretContainer(root, range.startContainer);
  });
  if (!valid) return null;

  const rect = rectFromRange(range);
  if (!rect || !isRectOverRoot(root, rect)) return null;
  return rect;
};

const assetChipAtPoint = (event) => {
  const target = event.target;
  const element =
    target?.nodeType === Node.ELEMENT_NODE ? target : target?.parentElement;
  return element?.closest?.(`[${ASSET_MENTION_ID_ATTRIBUTE}]`) || null;
};

// Puts the caret where the asset was dropped so the pill lands under the cursor.
// Falls back to the end of the document when the point isn't a valid caret
// position (see $isValidCaretContainer above — same guard applies here: a
// mention chip, empty space with nothing to hit-test, or outside `root` entirely
// all land on this fallback rather than somewhere wrong).
const $selectDropPoint = (root, event) => {
  const { x, y } = clampToRoot(root, event.clientX, event.clientY);
  const range = caretRangeAtPoint(x, y);
  if (range && $isValidCaretContainer(root, range.startContainer)) {
    const node = $getNearestNodeFromDOMNode(range.startContainer);
    const offset = Math.min(range.startOffset, node.getTextContentSize());
    node.select(offset, offset);
    return;
  }
  $getRoot().selectEnd();
};

const $assetMentionNode = (asset) =>
  $createMentionNode("asset", asset._id, asset.name || "Asset", {
    mediaType: asset.mediaType || "image",
  });

// Swaps every mention of `replacedAssetId` for `asset` — dropping onto a chip
// re-points all of that asset's appearances, not just the one under the pointer.
const $replaceEveryMentionOfAsset = (replacedAssetId, asset) => {
  const mentions = $nodesOfType(MentionNode).filter(
    (node) =>
      node.getMentionType() === "asset" &&
      node.getMentionId() === replacedAssetId,
  );
  mentions.forEach((node) => node.replace($assetMentionNode(asset)));
  return mentions.length;
};

/**
 * Inserts an asset mention when an asset is dropped on the editor, and shows
 * where it will land while the drag is in flight — a caret line at the insertion
 * point, or an outline over an existing asset chip when the drop would replace
 * every mention of that asset.
 *
 * `resolveAsset(id)` supplies the name and mediaType. `onAssetsDropped(assets)`
 * is handed the resolved assets so a host can mirror the drop elsewhere.
 *
 * Uses native listeners on the editor root rather than DROP_COMMAND: Lexical's
 * own drop handling runs on the same event, and dragenter/dragover both need
 * preventDefault before the browser will deliver a drop at all.
 */
export default function AssetMentionPlugin({ resolveAsset, onAssetsDropped }) {
  const [editor] = useLexicalComposerContext();
  const { registerAssets } = useEditorAssets();

  // `caret` carries a rect to draw the insertion line at; `replaceRect` carries
  // the chip box to outline. Exactly one is set while a drag is over the editor.
  const [dropTarget, setDropTarget] = useState(null);

  // Callers pass a fresh arrow each render and the app re-renders mid-drag, so the
  // listeners are registered once against a ref rather than re-bound every render.
  const resolveRef = useRef(resolveAsset);
  resolveRef.current = resolveAsset;
  const droppedRef = useRef(onAssetsDropped);
  droppedRef.current = onAssetsDropped;
  const registerRef = useRef(registerAssets);
  registerRef.current = registerAssets;

  useEffect(() => {
    let root = null;

    const allowDrag = (event) => {
      if (!isAssetDrag(event.dataTransfer)) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "copy";

      const chip = assetChipAtPoint(event);
      if (chip) {
        const { top, left, width, height } = chip.getBoundingClientRect();
        setDropTarget({ mode: "replace", top, left, width, height });
        return;
      }

      const caret =
        root && caretRectAtPoint(editor, root, event.clientX, event.clientY);
      setDropTarget(caret ? { mode: "insert", ...caret } : null);
    };

    const clearDropTarget = () => setDropTarget(null);

    const onDrop = (event) => {
      if (!isAssetDrag(event.dataTransfer)) return;
      event.preventDefault();
      event.stopPropagation();
      setDropTarget(null);

      const resolve = resolveRef.current;
      if (!resolve) return;

      const assets = draggedAssetIds(event.dataTransfer)
        .map(resolve)
        .filter(Boolean);
      if (assets.length === 0) return;

      registerRef.current?.(assets);

      const chip = assetChipAtPoint(event);
      const replacedAssetId = chip?.getAttribute(ASSET_MENTION_ID_ATTRIBUTE);

      editor.update(() => {
        if (replacedAssetId) {
          $replaceEveryMentionOfAsset(replacedAssetId, assets[0]);
          return;
        }
        if (root) $selectDropPoint(root, event);
        $insertNodes(assets.map($assetMentionNode));
      });

      droppedRef.current?.(assets);
    };

    const attach = (element) => {
      element.addEventListener("dragenter", allowDrag);
      element.addEventListener("dragover", allowDrag);
      element.addEventListener("dragleave", clearDropTarget);
      element.addEventListener("drop", onDrop);
    };
    const detach = (element) => {
      element.removeEventListener("dragenter", allowDrag);
      element.removeEventListener("dragover", allowDrag);
      element.removeEventListener("dragleave", clearDropTarget);
      element.removeEventListener("drop", onDrop);
    };

    const unregister = editor.registerRootListener((nextRoot) => {
      if (root) detach(root);
      root = nextRoot;
      if (root) attach(root);
    });

    // A drag that ends outside the editor never fires our drop handler, so the
    // indicator is cleared from the document-level end of the drag as well.
    window.addEventListener("dragend", clearDropTarget);
    window.addEventListener("drop", clearDropTarget);

    return () => {
      if (root) detach(root);
      unregister();
      window.removeEventListener("dragend", clearDropTarget);
      window.removeEventListener("drop", clearDropTarget);
    };
  }, [editor]);

  if (!dropTarget) return null;

  if (dropTarget.mode === "replace") {
    return (
      <div
        className="fixed z-50 pointer-events-none rounded ring-2 ring-primary bg-primary/20"
        style={{
          top: dropTarget.top,
          left: dropTarget.left,
          width: dropTarget.width,
          height: dropTarget.height,
        }}
      />
    );
  }

  return (
    <div
      className="fixed z-50 pointer-events-none w-0.5 bg-primary animate-pulse"
      style={{
        top: dropTarget.top,
        left: dropTarget.left,
        height: dropTarget.height,
      }}
    />
  );
}
